import React, { useCallback, useEffect, useState } from 'react';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash-es';

import StatusTag from 'components/workflow/StatusTag';
import { classnames } from 'lib/classnames';
import useCurrentUser from 'lib/hooks/useCurrentUser';
import useI18n from 'lib/hooks/useI18n';
import { FieldValue } from 'lib/types/field';
import { ObjectId } from 'lib/types/models';

import { FieldValuesType, TransitionType } from '../../type';
import { defaultValueFormat, getFieldsConfiguration } from '../../utils';

import TransitionField from './TransitionField';

import styles from './TransitionConfirm.module.less';

const cx = classnames(styles);
interface ItemsTableProps {
  data: TransitionType;
  fields: string[];
  fieldValues: FieldValuesType;
  onChange: (key: string, data: FieldValue) => void;
  handleCheckChange: (key: string, data: FieldValue) => void;
  workspaceId: ObjectId;
}

const TransitionConfirm: React.VFC<ItemsTableProps> = ({
  data,
  workspaceId,
  fields,
  fieldValues,
  onChange,
  handleCheckChange,
}) => {
  const { t } = useI18n();
  const [fieldConfig, setFieldConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useCurrentUser();
  const initFields = useCallback(async fields => {
    let res = await getFieldsConfiguration(fields);
    res = res.filter(i => fields?.includes(i.key));
    // 设置初始值
    setLoading(false);
    // 设置默认值
    const hasDefaultFields = res.filter(i => !isEmpty(i.property?.defaultValue));
    if (hasDefaultFields?.length) {
      // 如果有用户字段，而且是当前用户，需要修改数据
      const initData = {};
      hasDefaultFields.forEach(item => {
        initData[item.key] = {
          key: item.key,
          checked: false,
          data: defaultValueFormat(item, { currentUser: currentUser?.toJSON() }),
        };
      });
      onChange('initAllField', initData);
    }
    setFieldConfig(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fields?.length) {
      initFields(fields);
    } else {
      setFieldConfig([]);
      setLoading(false);
    }
  }, [fields, initFields]);

  return (
    <div>
      <div className={cx('transition-confirm-header')}>
        <div className={cx('transition-confirm-header-row')}>
          <span className={cx('transition-confirm-header-label')}>
            {t('pages.itemsBatchOperate.columns.transition.workflow')}
          </span>
          <span className={cx('transition-confirm-header-value')}>{data.workflowName}</span>
        </div>
        <div className={cx('transition-confirm-header-row')}>
          <span className={cx('transition-confirm-header-label')}>
            {t('pages.itemsBatchOperate.columns.transition.selectTransition')}
          </span>
          <span className={cx('transition-confirm-header-value')}>{data.name}</span>
        </div>
        <div className={cx('transition-confirm-header-row')}>
          <span className={cx('transition-confirm-header-label')}>
            {t('pages.itemsBatchOperate.columns.transition.statusTransition')}
          </span>
          <span className={cx('transition-confirm-header-value')}>
            <StatusTag data={data.source} isAny={data.anyTag} />
            <span className={cx('status-arrow')}> → </span>
            <StatusTag data={data.target} />
          </span>
        </div>
        {data.flowHandler?.length && (
          <div className={cx('transition-confirm-header-row')}>
            <span className={cx('transition-confirm-header-label')}>
              {t('pages.itemsBatchOperate.columns.transition.nextFlowHandler')}
            </span>
            <span className={cx('transition-confirm-header-value')}>
              {data.flowHandler.map(user => user.label).join(', ')}
            </span>
          </div>
        )}
      </div>
      {/* 没有流转界面 */}
      {!fieldConfig.length && !loading ? (
        <p className={cx('transition-confirm-empty')}>
          <InfoCircleOutlined />
          <span className={cx('transition-confirm-empty-tip')}>
            {t('pages.itemsBatchOperate.columns.transition.noFields')}
          </span>
        </p>
      ) : (
        <p className={cx('transition-confirm-tip')}>{t('pages.itemsBatchOperate.columns.transition.fields')}</p>
      )}
      {loading && <LoadingOutlined className={cx('transition-confirm-loading')} />}
      {/* 带界面的流转 */}
      <div className={cx('transition-confirm-field-wrap')}>
        {fieldConfig.map(field => {
          const fieldValue = fieldValues[field.key];
          return (
            <TransitionField
              workspaceId={workspaceId}
              key={field.objectId}
              value={fieldValue?.data}
              checked={fieldValue?.checked}
              field={field}
              fieldChange={onChange}
              handleCheckChange={handleCheckChange}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TransitionConfirm;
